import React, { useEffect, useState } from "react";
import { apiRequestWithAuth } from "../../utils/api";
import Alert from "../../components/ui/alert/Alert";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import {useNavigate, useParams} from "react-router-dom";
import {CheckCircle2Icon} from "lucide-react";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import {Textarea} from "@mantine/core";
import ComponentCard from "../../components/common/ComponentCard";
import MyEditor from "../../components/form/input/MyEditor";
import {ChatIcon} from "../../icons";
import toast, { Toaster } from 'react-hot-toast';

const QuizAttempt = () => {
    const { id } = useParams<{ id: string }>();
    const defaultForm = {
        id: "",
        author: {
            id: "",
            name: "",
            email: "",
            phoneNumber: ""
        },
        quiz: {
            id: "",
            title: "",
            shortDescription: "",
            author: {
                id: "",
                name: "",
                email: "",
                phoneNumber: ""
            },
        },
        score: 0,
        note: "",
        startedAt: "",
        completedAt: "",
        createTime: "",
        lastUpdate: "",
        answersQuestion: []
    };

    interface question {
        id: number,
        uid: "",
        title: "",
        shortDescription: "",
        content: "",
        answerText: "",
        answers: [
            answer
        ],
        fileId: "",
        file: {
            publicPath : ""
        },
        image: "",
        cover: "",
        media: "",
        mediaType: "",
        quizId: "",
        isGroup: "",
        parentId: "",
        childs: [
            child
        ],
        score: "",
        type: number,
        typeName: "",
        orderNo: "",
        result: {
            id: "",
            userQuizId: "",
            questionId: "",
            answerId: number,
            answer: "",
            attachmentId: "",
            isCorrect: "",
            note: "",
            createTime: "",
            lastUpdate: "",
        },
        createTime: "",
        lastUpdate: "",
    }

    interface child {
        id: number,
        uid: "",
        title: "",
        shortDescription: "",
        content: "",
        answerText: "",
        answers: [
            answer
        ],
        fileId: "",
        file: "",
        image: "",
        cover: "",
        media: "",
        mediaType: "",
        quizId: "",
        isGroup: "",
        parentId: "",
        score: "",
        type: number,
        typeName: "",
        orderNo: "",
        result: {
            id: number,
            userQuizId: "",
            questionId: "",
            answerId: number,
            answer: "",
            attachmentId: "",
            isCorrect: "",
            note: "",
            createTime: "",
            lastUpdate: "",
        },
        createTime: "",
        lastUpdate: "",
    }

    interface answer {
        id: number,
        uid: "",
        title: "",
        questionId: "",
        isCorrect: number,
        fileId: "",
        media: "",
        mediaType: "",
        type: "",
        orderNo: "",
        createTime: "",
        lastUpdate: ""
    }

    interface result {
        id: number,
        userQuizId: "",
        questionId: "",
        answerId: "",
        answer: "",
        attachmentId: "",
        isCorrect: "",
        note: "",
        createTime: "",
        lastUpdate: "",
    }

    const [formData, setFormData] = useState(defaultForm);
    const [questions, setQuestions] = useState<question[]>([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [showComment, setShowComment] = useState<Record<number, boolean>>({});
    const [comments, setComments] = useState<Record<number, string>>({});
    const [total, setTotal] = useState<number>(0);
    const navigate = useNavigate();

    type ToastType = 'success' | 'error' | 'loading';
    const notify = (msg: string, type: ToastType = 'success') => {
        const options = {
            duration: 4000,
            position: 'top-right' as const,
        };

        switch (type) {
            case 'success':
                toast.success(msg, options);
                break;
            case 'error':
                toast.error(msg, options);
                break;
            case 'loading':
                toast.loading(msg, options);
                break;
            default:
                toast(msg, options);
        }
    };

    // Fetch existing course
    useEffect(() => {
        const fetchAttempt = async () => {
            try {
                const res = await apiRequestWithAuth(`/quiz/my/attempt/${id}`, "GET", {});
                const data = res.result;

                setFormData(
                    {
                        id: data.id,
                        author: {
                            id: data.author?.id || "",
                            name: data.author?.name || "",
                            email: data.author?.email || "",
                            phoneNumber: data.author?.phoneNumber || "N/A"
                        },
                        quiz: {
                            id: data.quiz?.id || "",
                            title: data.quiz?.title || "",
                            shortDescription: data.quiz?.shortDescription || "",
                            author: {
                                id: data.quiz?.author?.id || "",
                                name: data.quiz?.author?.name || "",
                                email: data.quiz?.author?.email || "",
                                phoneNumber: data.quiz?.author?.phoneNumber || "N/A"
                            }
                        },
                        score: data.score,
                        note: data.note,
                        startedAt: data.startedAt,
                        completedAt: data.completedAt,
                        createTime: data.createTime,
                        lastUpdate: data.lastUpdate,
                        answersQuestion: data.answersQuestion || []
                    }
                );

                setQuestions(data.quiz?.questions);


            } catch (error) {
                console.error("Lỗi khi lấy dữ liệu slider:", error);
            }
        };

        if (id) fetchAttempt();
    }, [id]);

    useEffect(() => {
        let total = 0;
        questions.forEach((question) => {
            if (question.childs && question.childs.length > 0) {
                question.childs.forEach(() => {
                    total++;
                });
            } else if (question.result) {
                total++;
            }
        });
        setTotal(total);
    }, [questions]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = "metaDescription";
        const value = e.target ? e.target.value : "";
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = "shortDescription";
        const value = e.target ? e.target.value : "";
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSave = async () => {
        try {
            await apiRequestWithAuth(`/quiz/my/attempt/${id}`, "PUT", formData);
            setOpenSnackbar(true);
            setTimeout(() => navigate(`/quiz/attempt/${id}/detail`), 1000);
        } catch (error) {
            console.error("Update post failed", error);
        }
    };

    const handleSubmitComment = async (userQuizId: number) => {
        const comment = comments[userQuizId];

        if (!comment?.trim()) {
            alert("Vui lòng nhập nhận xét.");
            return;
        }

        try {
            const frmComment = new FormData();
            frmComment.append("note", comment);
            const response = await apiRequestWithAuth(`/quiz/my/attempt/${id}/answer/${userQuizId}`, "POST", frmComment);
            if (response.messageCode === 200) {
                setShowComment(prev => ({
                    ...prev,
                    [userQuizId]: !prev[userQuizId]
                }));
                notify('Gửi nhận xét thành công');
                console.log(comments[userQuizId]);
            } else {
                alert("Gửi nhận xét thất bại. Vui lòng thử lại.");
            }
        } catch (error) {
            console.error("Lỗi khi gửi nhận xét:", error);
            alert("Gửi nhận xét thất bại.");
        }
    };

    const toggleComment = (userQuizId: number) => {
        setShowComment(prev => ({
            ...prev,
            [userQuizId]: !prev[userQuizId]
        }));
    };

    const handleCommentChange = (userQuizId: number, value: string) => {
        setComments(prev => ({
            ...prev,
            [userQuizId]: value,
        }));
    };

    const strToArray = (str: string) => {
        return (str && str.length > 0) ? str.split(',').map(s => parseInt(s.trim(), 10)) : [];
    };

    return (
        <div>
            <PageMeta title="Chi tiết bài làm" description="Trang chi tiết bài làm" />
            <PageBreadcrumb pageTitle="Chi tiết bài làm" />
            <Toaster />

            {openSnackbar && (
                <Alert
                    variant="success"
                    title="Cập nhật đánh giá thành công"
                    message="Bạn đã cập nhật đánh giá thành công."
                />
            )}

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1 attempt-detail">
                <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] ">
                    <div className="px-6 py-5">
                        <div
                            className="flex items-center justify-between rounded-2xl bg-white dark:bg-white/[0.03]">
                            <div className="flex items-center gap-4">
                                <div
                                    className="flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-orange-500/[0.08] text-orange-500">
                                    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none"
                                         xmlns="http://www.w3.org/2000/svg" className="size-6">
                                        <path fillRule="evenodd" clipRule="evenodd"
                                              d="M5.5 3.25C4.25736 3.25 3.25 4.25736 3.25 5.5V8.99998C3.25 10.2426 4.25736 11.25 5.5 11.25H9C10.2426 11.25 11.25 10.2426 11.25 8.99998V5.5C11.25 4.25736 10.2426 3.25 9 3.25H5.5ZM4.75 5.5C4.75 5.08579 5.08579 4.75 5.5 4.75H9C9.41421 4.75 9.75 5.08579 9.75 5.5V8.99998C9.75 9.41419 9.41421 9.74998 9 9.74998H5.5C5.08579 9.74998 4.75 9.41419 4.75 8.99998V5.5ZM5.5 12.75C4.25736 12.75 3.25 13.7574 3.25 15V18.5C3.25 19.7426 4.25736 20.75 5.5 20.75H9C10.2426 20.75 11.25 19.7427 11.25 18.5V15C11.25 13.7574 10.2426 12.75 9 12.75H5.5ZM4.75 15C4.75 14.5858 5.08579 14.25 5.5 14.25H9C9.41421 14.25 9.75 14.5858 9.75 15V18.5C9.75 18.9142 9.41421 19.25 9 19.25H5.5C5.08579 19.25 4.75 18.9142 4.75 18.5V15ZM12.75 5.5C12.75 4.25736 13.7574 3.25 15 3.25H18.5C19.7426 3.25 20.75 4.25736 20.75 5.5V8.99998C20.75 10.2426 19.7426 11.25 18.5 11.25H15C13.7574 11.25 12.75 10.2426 12.75 8.99998V5.5ZM15 4.75C14.5858 4.75 14.25 5.08579 14.25 5.5V8.99998C14.25 9.41419 14.5858 9.74998 15 9.74998H18.5C18.9142 9.74998 19.25 9.41419 19.25 8.99998V5.5C19.25 5.08579 18.9142 4.75 18.5 4.75H15ZM15 12.75C13.7574 12.75 12.75 13.7574 12.75 15V18.5C12.75 19.7426 13.7574 20.75 15 20.75H18.5C19.7426 20.75 20.75 19.7427 20.75 18.5V15C20.75 13.7574 19.7426 12.75 18.5 12.75H15ZM14.25 15C14.25 14.5858 14.5858 14.25 15 14.25H18.5C18.9142 14.25 19.25 14.5858 19.25 15V18.5C19.25 18.9142 18.9142 19.25 18.5 19.25H15C14.5858 19.25 14.25 18.9142 14.25 18.5V15Z"
                                              fill="currentColor">
                                        </path>
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="mb-1 text-sm font-medium text-gray-800 dark:text-white/90">Kết quả
                                        bài làm của học sinh: {formData.author.name}</h4>
                                    <div className="block text-sm text-gray-500 dark:text-gray-400">
                                        <span className="mr-2">Trạng thái: </span>
                                        <strong
                                            className={`inline-flex items-center px-2.5 py-0.5 justify-center gap-1 rounded-full font-medium text-theme-xs bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-600`}>
                                            Chưa hoàn thành
                                        </strong>
                                    </div>
                                </div>
                            </div>
                            {/*<div>
                                <span className="block mb-1 text-sm text-right text-gray-500 dark:text-gray-400">Mã đơn hàng</span>
                                <strong className="block text-sm text-right text-black-500 dark:text-black-400">ID:
                                    #{formData.id}</strong>
                            </div>*/}
                        </div>
                    </div>
                    <div className="border-t border-gray-100 dark:border-gray-800"></div>
                    <div className="px-6 py-5">
                        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-7 2xl:gap-x-32">
                                    <div>
                                        <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                                            Tác giả:
                                        </p>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                                            {formData.quiz?.author?.name || "N/A"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                                            Bài kiểm tra:
                                        </p>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                                            {formData.quiz?.title || "N/A"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-7 2xl:gap-x-32">
                                    <div>
                                        <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                                            Số câu hỏi:
                                        </p>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                                            {total}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                                            Học sinh làm:
                                        </p>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                                            {formData.answersQuestion.length || "N/A"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90" x-text="pageName">Kết quả bài
                        làm</h2>
                </div>

                {questions.map((question, index) => (
                    <>
                        <div
                            className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] "
                        >
                            <div className="px-6 py-5">
                                <div className="flex items-start gap-4">
                                    <div
                                        className="flex h-[30px] w-[30px] items-center justify-center rounded-xl bg-success-500/[0.08] text-success-500"
                                    >
                                        {index + 1}
                                    </div>
                                    <div>
                                        <span className="block text-sm text-nowrap text-gray-500 dark:text-gray-400">Câu hỏi: {question.typeName}</span>
                                        <div className="mb-5 font-bold"
                                             dangerouslySetInnerHTML={{__html: question.title}}></div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-2 border-t border-gray-100 dark:border-gray-800 sm:p-2"></div>
                            <div className="p-4">
                                {question.type === 8 && (
                                    <div className="mt-2 mb-4">
                                        <audio controls src={question.file?.publicPath} className="w-full">
                                            Trình duyệt của bạn không hỗ trợ audio.
                                        </audio>
                                    </div>
                                )}

                                {question.childs && question.childs.length > 0 ? (
                                    <div className="block">
                                        {question.childs?.map((child, ind) => (
                                            <>
                                                <div
                                                    className="p-5 mb-5 border border-gray-200 rounded-2xl dark:border-gray-800">
                                                    <span
                                                        className="block text-sm text-nowrap text-gray-500 dark:text-gray-400">(Câu hỏi: {child.typeName})</span>
                                                    <h3 className="font-bold">Câu: {ind + 1}</h3>
                                                    <div className="mt-2"
                                                         dangerouslySetInnerHTML={{__html: child.title}}></div>
                                                    <div className="answers mt-3" key={child.id}>
                                                        {[1, 2, 3].includes(child.type) ? (
                                                            <div>
                                                                {child.answers?.map((answer) => (
                                                                    <>
                                                                        <div className="mt-4 flex items-start gap-3">
                                                                            <div>
                                                                                {[1, 3].includes(child.type) ? (
                                                                                    <>
                                                                                        {(answer.id === child.result?.answerId && answer.isCorrect === 1) ? (
                                                                                            <button
                                                                                                className={`text-sm px-1.5 py-1.5 rounded-md bg-success-100 text-success-600 hover:bg-success-200 transition`}>
                                                                                                <CheckCircle2Icon/>
                                                                                            </button>
                                                                                        ) : (
                                                                                            <>
                                                                                                {answer.id === child.result?.answerId && answer.isCorrect !== 1 ? (
                                                                                                    <button
                                                                                                        className={`text-sm px-1.5 py-1.5 rounded-md bg-error-100 text-error-600 hover:bg-error-200 transition`}>
                                                                                                        <CheckCircle2Icon/>
                                                                                                    </button>
                                                                                                ) : (
                                                                                                    <button
                                                                                                        className={`text-sm px-1.5 py-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition`}>
                                                                                                        <CheckCircle2Icon/>
                                                                                                    </button>
                                                                                                )}
                                                                                            </>
                                                                                        )}
                                                                                    </>
                                                                                ) : null}

                                                                                {child.type === 2 ? (
                                                                                    <>
                                                                                        {((strToArray(child.result?.answer)).includes(Number(answer.id)) && answer.isCorrect === 1) ? (
                                                                                            <button
                                                                                                className={`text-sm px-1.5 py-1.5 rounded-md bg-success-100 text-success-600 hover:bg-success-200 transition`}>
                                                                                                <CheckCircle2Icon/>
                                                                                            </button>
                                                                                        ) : (
                                                                                            <>
                                                                                                {((strToArray(child.result?.answer)).includes(Number(answer.id)) && answer.isCorrect !== 1) ? (
                                                                                                    <button
                                                                                                        className={`text-sm px-1.5 py-1.5 rounded-md bg-error-100 text-error-600 hover:bg-error-200 transition`}>
                                                                                                        <CheckCircle2Icon/>
                                                                                                    </button>
                                                                                                ) : (
                                                                                                    <button
                                                                                                        className={`text-sm px-1.5 py-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition`}>
                                                                                                        <CheckCircle2Icon/>
                                                                                                    </button>
                                                                                                )}
                                                                                            </>
                                                                                        )}
                                                                                    </>
                                                                                ) : null}
                                                                            </div>
                                                                            <div
                                                                                className={`rounded-xl block border pt-2 pb-1 px-4 ${answer.isCorrect === 1 ? 'border-success-500 bg-success-50 dark:border-success-500/30 dark:bg-success-500/15' : 'border-default-500 bg-default-50 dark:border-default-500/30 dark:bg-default-500/15'}`}>
                                                                                <div className="flex items-start gap-3">
                                                                                    <div
                                                                                        className={`-mt-0.5 ${answer.isCorrect === 1 ? 'text-success-500' : 'text-default-500'}`}>
                                                                                        <CheckCircle2Icon/>
                                                                                    </div>
                                                                                    <div>
                                                                                        <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90" dangerouslySetInnerHTML={{__html: answer.title}}></h4>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                        {child.type === 4 ? (
                                                            <>
                                                                <div className="mt-4">
                                                                    <span
                                                                        className="font-semibold text-gray-800 dark:text-white/90">Câu trả lời: </span>
                                                                    <span
                                                                        className="text-gray-600 dark:text-gray-400">{child.result?.answer}</span>
                                                                </div>
                                                                <div className="mt-4">
                                                                    <span
                                                                        className="font-semibold text-gray-800 dark:text-white/90">Đáp án đúng: </span>
                                                                    <span
                                                                        className="text-gray-600 dark:text-gray-400">{child.answerText}</span>
                                                                </div>
                                                            </>
                                                        ) : null}
                                                        {child.type === 5 && child.result.answer ? (
                                                            <>
                                                                <div className="mt-4 mb-2">
                                                                    <span
                                                                        className="font-semibold text-gray-800 dark:text-white/90">Câu trả lời: </span>
                                                                </div>
                                                                {strToArray(child.result.answer).map((itemId) => (
                                                                    <>
                                                                        {child.answers.map((answer) => (
                                                                            <>
                                                                                {answer.id === itemId ? (
                                                                                    <div
                                                                                        className={`rounded-xl inline-block border mr-2 pt-2 pb-1 px-4 border-default-500 bg-default-50 dark:border-default-500/30 dark:bg-default-500/15`}>
                                                                                        <div
                                                                                            className="flex items-start gap-3">
                                                                                            <div
                                                                                                className={`-mt-0.5 text-default-500`}>
                                                                                                <CheckCircle2Icon/>
                                                                                            </div>
                                                                                            <div>
                                                                                                <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90"
                                                                                                    dangerouslySetInnerHTML={{__html: answer.title}}></h4>

                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ) : null}
                                                                            </>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                            </>
                                                        ) : null}
                                                        {child.type === 5 && child.answerText ? (
                                                            <>
                                                                <div className="mt-4 mb-2">
                                                                    <span
                                                                        className="font-semibold text-gray-800 dark:text-white/90">Đáp án đúng: </span>
                                                                </div>
                                                                {strToArray(child.answerText).map((itemId) => (
                                                                    <>
                                                                        {child.answers.map((answer) => (
                                                                            <>
                                                                                {answer.id === itemId ? (
                                                                                    <div
                                                                                        className={`rounded-xl inline-block border mr-2 pt-2 pb-1 px-4 border-default-500 bg-default-50 dark:border-default-500/30 dark:bg-default-500/15`}>
                                                                                        <div
                                                                                            className="flex items-start gap-3">
                                                                                            <div
                                                                                                className={`-mt-0.5 text-default-500`}>
                                                                                                <CheckCircle2Icon/>
                                                                                            </div>
                                                                                            <div>
                                                                                                <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90"
                                                                                                    dangerouslySetInnerHTML={{__html: answer.title}}></h4>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ) : null}
                                                                            </>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                            </>
                                                        ) : null}
                                                        {child.type === 7 ? (
                                                            <>
                                                                <div className="mt-4">
                                                                    <span
                                                                        className="font-semibold text-gray-800 dark:text-white/90">Câu trả lời: </span>
                                                                    <div className="mt-2"
                                                                         dangerouslySetInnerHTML={{__html: child.result?.answer}}></div>
                                                                </div>
                                                                <div className="mt-4">
                                                                    <span
                                                                        className="font-semibold text-gray-800 dark:text-white/90">Đáp án đúng: </span>
                                                                    <div className="mt-2"
                                                                         dangerouslySetInnerHTML={{__html: child.answerText}}></div>
                                                                </div>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                    {child.result ? (
                                                        <>
                                                            <div className="mt-5">
                                                                {(child.result.note || comments[child.result.id]) && (
                                                                    <>
                                                                        <div className="flex items-start gap-3 mb-3">
                                                                            <div className="-mt-0.5 text-gray-dark-500">
                                                                                <ChatIcon/>
                                                                            </div>
                                                                            <div>
                                                                                <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">{comments[child.result.id] || child.result.note}</h4>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleComment(child.result.id)}
                                                                    className="text-blue-600 text-sm underline mb-2"
                                                                >
                                                                    {showComment[child.result.id] ? "Ẩn nhận xét" : "Thêm nhận xét"}
                                                                </button>
                                                                {showComment[child.result.id] && (
                                                                    <>
                                                                        {/*<h4>Giáo viên nhận xét</h4>*/}
                                                                        <div className="mb-4 grid grid-cols-3 gap-4">
                                                                            <div className="col-span-2">
                                                                                <Textarea
                                                                                    value={comments[child.result.id]}
                                                                                    onChange={(e) => handleCommentChange(child.result.id, e.target.value)}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleSubmitComment(child.result.id)}
                                                                            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300"
                                                                        >
                                                                            Gửi đánh giá
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>

                                                        </>
                                                    ) : null}
                                                </div>
                                            </>
                                        ))}
                                    </div>
                                ) : null}

                                {question.answers.length > 0 ? (
                                    <>
                                    <div
                                            className="p-5 mb-5 border border-gray-200 rounded-2xl dark:border-gray-800">
                                            <div className="answers mt-3">
                                                {[1, 2, 3].includes(question.type) ? (
                                                    <div>
                                                    {question.answers?.map((answer) => (
                                                            <>
                                                                <div className="mt-4 flex items-start gap-3">
                                                                    <div>
                                                                        {[1, 3].includes(question.type) ? (
                                                                            <>
                                                                                {(answer.id === question.result?.answerId && answer.isCorrect === 1) ? (
                                                                                    <button
                                                                                        className={`text-sm px-1.5 py-1.5 rounded-md bg-success-100 text-success-600 hover:bg-success-200 transition`}>
                                                                                        <CheckCircle2Icon/>
                                                                                    </button>
                                                                                ) : (
                                                                                    <>
                                                                                        {answer.id === question.result?.answerId && answer.isCorrect !== 1 ? (
                                                                                            <button
                                                                                                className={`text-sm px-1.5 py-1.5 rounded-md bg-error-100 text-error-600 hover:bg-error-200 transition`}>
                                                                                                <CheckCircle2Icon/>
                                                                                            </button>
                                                                                        ) : (
                                                                                            <button
                                                                                                className={`text-sm px-1.5 py-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition`}>
                                                                                                <CheckCircle2Icon/>
                                                                                            </button>
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </>
                                                                        ) : null}

                                                                        {question.type === 2 ? (
                                                                            <>
                                                                                {((strToArray(question.result?.answer)).includes(Number(answer.id)) && answer.isCorrect === 1) ? (
                                                                                    <button
                                                                                        className={`text-sm px-1.5 py-1.5 rounded-md bg-success-100 text-success-600 hover:bg-success-200 transition`}>
                                                                                        <CheckCircle2Icon/>
                                                                                    </button>
                                                                                ) : (
                                                                                    <>
                                                                                        {((strToArray(question.result?.answer)).includes(Number(answer.id)) && answer.isCorrect !== 1) ? (
                                                                                            <button
                                                                                                className={`text-sm px-1.5 py-1.5 rounded-md bg-error-100 text-error-600 hover:bg-error-200 transition`}>
                                                                                                <CheckCircle2Icon/>
                                                                                            </button>
                                                                                        ) : (
                                                                                            <button
                                                                                                className={`text-sm px-1.5 py-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition`}>
                                                                                                <CheckCircle2Icon/>
                                                                                            </button>
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </>
                                                                        ) : null}
                                                                    </div>
                                                                    <div
                                                                        className={`rounded-xl block border pt-2 pb-1 px-4 ${answer.isCorrect === 1 ? 'border-success-500 bg-success-50 dark:border-success-500/30 dark:bg-success-500/15' : 'border-default-500 bg-default-50 dark:border-default-500/30 dark:bg-default-500/15'}`}>
                                                                        <div className="flex items-start gap-3">
                                                                            <div
                                                                                className={`-mt-0.5 ${answer.isCorrect === 1 ? 'text-success-500' : 'text-default-500'}`}>
                                                                                <CheckCircle2Icon/>
                                                                            </div>
                                                                            <div>
                                                                                <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90"
                                                                                    dangerouslySetInnerHTML={{__html: answer.title}}></h4>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        ))}
                                                    </div>
                                                ) : null}
                                                {question.type === 4 ? (
                                                    <>
                                                        <div className="mt-4">
                                                            <span
                                                                className="font-semibold text-gray-800 dark:text-white/90">Câu trả lời: </span>
                                                            <span
                                                                className="text-gray-600 dark:text-gray-400">{question.result?.answer}</span>
                                                        </div>
                                                        <div className="mt-4">
                                                            <span
                                                                className="font-semibold text-gray-800 dark:text-white/90">Đáp án đúng: </span>
                                                            <span
                                                                className="text-gray-600 dark:text-gray-400">{question.answerText}</span>
                                                        </div>
                                                    </>
                                                ) : null}
                                                {question.type === 5 && question.result.answer ? (
                                                    <>
                                                        <div className="mt-4 mb-2">
                                                            <span
                                                                className="font-semibold text-gray-800 dark:text-white/90">Câu trả lời: </span>
                                                        </div>
                                                        {strToArray(question.result.answer).map((itemId) => (
                                                            <>
                                                                {question.answers.map((answer) => (
                                                                    <>
                                                                        {answer.id === itemId ? (
                                                                            <div
                                                                                className={`rounded-xl inline-block border mr-2 pt-2 pb-1 px-4 border-default-500 bg-default-50 dark:border-default-500/30 dark:bg-default-500/15`}>
                                                                                <div
                                                                                    className="flex items-start gap-3">
                                                                                    <div
                                                                                        className={`-mt-0.5 text-default-500`}>
                                                                                        <CheckCircle2Icon/>
                                                                                    </div>
                                                                                    <div>
                                                                                        <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90"
                                                                                            dangerouslySetInnerHTML={{__html: answer.title}}></h4>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ) : null}
                                                                    </>
                                                                ))}
                                                            </>
                                                        ))}
                                                    </>
                                                ) : null}
                                                {question.type === 5 && question.answerText ? (
                                                    <>
                                                        <div className="mt-4 mb-2">
                                                            <span
                                                                className="font-semibold text-gray-800 dark:text-white/90">Đáp án đúng: </span>
                                                        </div>
                                                        {strToArray(question.answerText).map((itemId) => (
                                                            <>
                                                                {question.answers.map((answer) => (
                                                                    <>
                                                                        {answer.id === itemId ? (
                                                                            <div
                                                                                className={`rounded-xl inline-block border mr-2 pt-2 pb-1 px-4 border-default-500 bg-default-50 dark:border-default-500/30 dark:bg-default-500/15`}>
                                                                                <div
                                                                                    className="flex items-start gap-3">
                                                                                    <div
                                                                                        className={`-mt-0.5 text-default-500`}>
                                                                                        <CheckCircle2Icon/>
                                                                                    </div>
                                                                                    <div>
                                                                                        <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90"
                                                                                            dangerouslySetInnerHTML={{__html: answer.title}}></h4>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        ) : null}
                                                                    </>
                                                                ))}
                                                            </>
                                                        ))}
                                                    </>
                                                ) : null}
                                                {question.type === 7 ? (
                                                    <>
                                                        <div className="mt-4">
                                                            <span
                                                                className="font-semibold text-gray-800 dark:text-white/90">Câu trả lời: </span>
                                                            <div className="mt-2"
                                                                 dangerouslySetInnerHTML={{__html: question.result?.answer}}></div>
                                                        </div>
                                                        <div className="mt-4">
                                                            <span
                                                                className="font-semibold text-gray-800 dark:text-white/90">Đáp án đúng: </span>
                                                            <div className="mt-2"
                                                                 dangerouslySetInnerHTML={{__html: question.answerText}}></div>
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </>
                ))}

                <ComponentCard title="Nhận xét của giáo viên">
                    <div>
                        <Label>Nhận xét</Label>
                        <MyEditor
                            content={formData.note}
                            onBlur={(value) => handleChange({ target: { name: "note", value } } as any)}
                        />
                    </div>
                </ComponentCard>

                <button
                    onClick={handleSave}
                    className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300"
                >
                    Câp nhật đơn hàng
                </button>
            </div>
        </div>
    );
};

export default QuizAttempt;
